/**
 * Horizontal scroll-snap carousel with prev/next buttons.
 * Reusable across LP sections (ritual steps, advisory circle, etc.).
 *
 * Usage:
 * <horizontal-scroll-carousel data-card-step="325">
 *   <div data-viewport>...</div>
 *   <button data-nav="prev">...</button>
 *   <button data-nav="next">...</button>
 * </horizontal-scroll-carousel>
 *
 * Optional: data-card-step-desktop — step width at min-width 741px (defaults to data-card-step).
 * Optional: data-drag-scroll — lets a mouse drag the viewport (touch already
 * scrolls natively) and snaps to the nearest card on release. The section CSS
 * should set `user-select: none` on the viewport so the drag doesn't select text.
 *
 * With a [data-track] inside the viewport, the arrows scroll to the exact
 * start of the next/previous card instead of by a fixed step, and scroll
 * snapping is paused until the scroll settles. iOS Safari otherwise lands
 * between cards when the arrows are tapped quickly: each tap interrupts the
 * previous smooth scroll and mandatory snapping fights the new one.
 */

class HorizontalScrollCarousel extends HTMLElement {
  constructor() {
    super();
    this.viewport = null;
    this.prevBtn = null;
    this.nextBtn = null;
    this.scrollRaf = 0;
    this.targetLeft = null;
    this.settleTimer = 0;
    this.onScroll = this.onScroll.bind(this);
    this.onSettle = this.onSettle.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.drag = null;
    this.suppressClick = false;
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.desktopMq = window.matchMedia("(min-width: 741px)");
  }

  connectedCallback() {
    this.viewport = this.querySelector("[data-viewport]");
    this.prevBtn = this.querySelector('[data-nav="prev"]');
    this.nextBtn = this.querySelector('[data-nav="next"]');

    if (!this.viewport) return;

    this.viewport.addEventListener("scroll", this.onScroll, { passive: true });
    this.viewport.addEventListener("scrollend", this.onSettle);
    // A swipe takes over from any arrow scroll still in progress.
    this.viewport.addEventListener("touchstart", this.onSettle, { passive: true });
    window.addEventListener("resize", this.onResize, { passive: true });
    if (this.prevBtn) this.prevBtn.addEventListener("click", this.onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener("click", this.onNextClick);

    this.dragEnabled = this.hasAttribute("data-drag-scroll");
    if (this.dragEnabled) {
      this.viewport.addEventListener("pointerdown", this.onPointerDown);
      this.viewport.addEventListener("pointermove", this.onPointerMove);
      this.viewport.addEventListener("pointerup", this.onPointerUp);
      this.viewport.addEventListener("pointercancel", this.onPointerUp);
      this.viewport.addEventListener("click", this.onClickCapture, true);
      this.viewport.addEventListener("dragstart", this.onDragStart);
    }

    this.updateButtonStates();
  }

  disconnectedCallback() {
    if (this.viewport) {
      this.viewport.removeEventListener("scroll", this.onScroll);
      this.viewport.removeEventListener("scrollend", this.onSettle);
      this.viewport.removeEventListener("touchstart", this.onSettle);
      this.viewport.removeEventListener("pointerdown", this.onPointerDown);
      this.viewport.removeEventListener("pointermove", this.onPointerMove);
      this.viewport.removeEventListener("pointerup", this.onPointerUp);
      this.viewport.removeEventListener("pointercancel", this.onPointerUp);
      this.viewport.removeEventListener("click", this.onClickCapture, true);
      this.viewport.removeEventListener("dragstart", this.onDragStart);
    }
    window.removeEventListener("resize", this.onResize);
    if (this.prevBtn) this.prevBtn.removeEventListener("click", this.onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener("click", this.onNextClick);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    clearTimeout(this.settleTimer);
  }

  getCardStep() {
    const mobileStep = Number(this.dataset.cardStep) || 330;
    const desktopStep = Number(this.dataset.cardStepDesktop) || mobileStep;
    return this.desktopMq.matches ? desktopStep : mobileStep;
  }

  onPrevClick() {
    if (!this.viewport || this.isDisabled(this.prevBtn)) return;
    this.step(-1);
  }

  onNextClick() {
    if (!this.viewport || this.isDisabled(this.nextBtn)) return;
    this.step(1);
  }

  // Scroll position at which each card sits at the viewport's snap start.
  getSnapPositions() {
    const track = this.viewport.querySelector("[data-track]");
    if (!track || !track.children.length) return [];

    const viewportLeft = this.viewport.getBoundingClientRect().left;
    const paddingStart = parseFloat(getComputedStyle(this.viewport).scrollPaddingLeft) || 0;
    const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
    const positions = Array.from(track.children, (card) => {
      const left = this.viewport.scrollLeft + card.getBoundingClientRect().left - viewportLeft - paddingStart;
      return Math.max(0, Math.min(maxScroll, Math.round(left)));
    });

    return [...new Set(positions)].sort((a, b) => a - b);
  }

  step(direction) {
    const positions = this.getSnapPositions();
    if (!positions.length) {
      this.viewport.scrollBy({ left: direction * this.getCardStep(), behavior: "smooth" });
      return;
    }

    // Count rapid taps from where the previous tap was heading, not from the
    // half-way point the interrupted animation reached.
    const from = this.targetLeft !== null ? this.targetLeft : this.viewport.scrollLeft;
    const target =
      direction > 0
        ? positions.find((left) => left > from + 1)
        : positions.reverse().find((left) => left < from - 1);
    if (target === undefined) return;

    this.targetLeft = target;
    this.viewport.style.scrollSnapType = "none";
    this.viewport.scrollTo({ left: target, behavior: "smooth" });
    this.scheduleSettle();
  }

  // Fallback for browsers without `scrollend`: settle once scrolling stops.
  scheduleSettle() {
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(this.onSettle, 150);
  }

  onSettle(event) {
    if (this.targetLeft === null) return;
    // An interrupted animation can fire `scrollend` short of the new target;
    // the timer settles it once the current one really stops.
    if (event && event.type === "scrollend" && Math.abs(this.viewport.scrollLeft - this.targetLeft) > 2) return;
    clearTimeout(this.settleTimer);
    this.targetLeft = null;
    this.viewport.style.scrollSnapType = "";
    this.updateButtonStates();
  }

  // Mouse only: touch and trackpads already scroll the viewport natively.
  onPointerDown(event) {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    if (this.viewport.scrollWidth - this.viewport.clientWidth <= 0) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startLeft: this.viewport.scrollLeft,
      moved: false,
    };
  }

  onPointerMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const dx = event.clientX - this.drag.startX;

    // A few pixels of slack so a plain click still reaches links and buttons.
    if (!this.drag.moved) {
      if (Math.abs(dx) < 5) return;
      this.drag.moved = true;
      clearTimeout(this.settleTimer);
      this.targetLeft = null;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.style.scrollSnapType = "none";
      this.viewport.style.scrollBehavior = "auto";
      this.classList.add("is-dragging");
    }

    this.viewport.scrollLeft = this.drag.startLeft - dx;
  }

  onPointerUp(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return;
    const { moved, startLeft } = this.drag;
    this.drag = null;
    if (!moved) return;

    this.classList.remove("is-dragging");
    this.viewport.style.scrollBehavior = "";
    // The click that follows a drag must not activate whatever is under the cursor.
    this.suppressClick = true;
    setTimeout(() => {
      this.suppressClick = false;
    }, 0);

    const positions = this.getSnapPositions();
    if (!positions.length) {
      this.viewport.style.scrollSnapType = "";
      this.updateButtonStates();
      return;
    }

    // Land on the nearest card, but a deliberate drag always moves at least one.
    const current = this.viewport.scrollLeft;
    const nearest = (left) =>
      positions.reduce((best, pos) => (Math.abs(pos - left) < Math.abs(best - left) ? pos : best));
    let target = nearest(current);
    if (target === nearest(startLeft) && Math.abs(current - startLeft) > 50) {
      const next =
        current > startLeft
          ? positions.find((left) => left > target + 1)
          : [...positions].reverse().find((left) => left < target - 1);
      if (next !== undefined) target = next;
    }

    this.targetLeft = target;
    this.viewport.scrollTo({ left: target, behavior: "smooth" });
    this.scheduleSettle();
  }

  onClickCapture(event) {
    if (!this.suppressClick) return;
    event.preventDefault();
    event.stopPropagation();
  }

  // Stops the browser's native image/link drag from hijacking the gesture.
  onDragStart(event) {
    event.preventDefault();
  }

  onScroll() {
    if (this.targetLeft !== null) this.scheduleSettle();
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      this.updateButtonStates();
    });
  }

  onResize() {
    this.updateButtonStates();
  }

  updateButtonStates() {
    if (!this.viewport) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
    const maxScroll = scrollWidth - clientWidth;
    const tolerance = 1;

    const atStart = scrollLeft <= tolerance;
    const atEnd = scrollLeft >= maxScroll - tolerance || maxScroll <= 0;

    this.classList.toggle("is-scrollable", maxScroll > tolerance);

    if (this.prevBtn) this.setDisabled(this.prevBtn, atStart);
    if (this.nextBtn) this.setDisabled(this.nextBtn, atEnd);
  }

  isDisabled(btn) {
    return btn && btn.getAttribute("aria-disabled") === "true";
  }

  setDisabled(btn, disabled) {
    btn.classList.toggle("is-disabled", disabled);
    btn.setAttribute("aria-disabled", disabled ? "true" : "false");
  }
}

if (!customElements.get("horizontal-scroll-carousel")) {
  customElements.define("horizontal-scroll-carousel", HorizontalScrollCarousel);
}
